import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import { UserCheck, Clock, IndianRupee, Shield } from "lucide-react";
import { Reveal } from "@/components/site/motion";

const CHAPTERS = [
  {
    n: "01",
    kicker: "The crew",
    title: "Skilled, verified, on-site foreman.",
    body:
      "Every project runs under a lead foreman. Background-verified painters trained on prep, finish quality and site etiquette.",
    icon: UserCheck,
  },
  {
    n: "02",
    kicker: "The clock",
    title: "On-time. Every single time.",
    body:
      "Detailed day-by-day plan with checkpoints. 98% on-time delivery across 100+ Bangalore projects — you always know what happens next.",
    icon: Clock,
  },
  {
    n: "03",
    kicker: "The price",
    title: "Flat, itemised, no surprises.",
    body:
      "Fixed BHK packages, quoted in writing. What we quote is exactly what you pay — labour and paint included.",
    icon: IndianRupee,
  },
  {
    n: "04",
    kicker: "The paint",
    title: "Only originals: Asian Paints, Berger, Dulux.",
    body:
      "Sealed tins delivered from certified distributors, verified on-site before the first coat.",
    icon: Shield,
  },
];

export default function Manifesto() {
  const wrap = useRef(null);
  const { scrollYProgress } = useScroll({
    target: wrap,
    offset: ["start end", "end start"],
  });
  const reduced = useReducedMotion();
  const y = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [40, -40]);

  return (
    <section
      ref={wrap}
      className="relative py-24 md:py-36 bg-ink text-white overflow-hidden"
      data-testid="manifesto-section"
    >
      {/* atmospheric */}
      <motion.div
        aria-hidden
        className="absolute -top-40 -right-32 h-[520px] w-[520px] rounded-full bg-orange-500/25 blur-[120px]"
        style={{ y }}
      />
      <motion.div
        aria-hidden
        className="absolute -bottom-32 -left-24 h-[420px] w-[420px] rounded-full bg-orange-400/10 blur-[100px]"
        style={{ y }}
      />

      <div className="relative max-w-7xl mx-auto px-5 md:px-10">
        <div className="max-w-3xl">
          <Reveal className="text-[11px] font-bold tracking-[0.28em] uppercase text-orange-300">
            The Bangalore Paint Pros manifesto
          </Reveal>
          <Reveal
            as="h2"
            delay={0.05}
            className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-[68px] leading-[0.95] text-white"
          >
            Four rules we <span className="text-orange-400 italic">never</span> break.
          </Reveal>
          <Reveal delay={0.1} className="mt-6 text-white/70 text-base md:text-lg max-w-xl">
            No cutting corners. No shifting quotes. No mystery invoices. Just
            craftsmanship, on time, in writing.
          </Reveal>
        </div>

        <div className="mt-16 md:mt-24 grid gap-12 md:gap-20">
          {CHAPTERS.map((c, i) => (
            <ChapterRow key={c.n} c={c} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ChapterRow({ c, i }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const reduced = useReducedMotion();
  const nY = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [50, -50]);

  return (
    <div
      ref={ref}
      className="relative grid grid-cols-12 gap-4 md:gap-8 border-t border-white/10 pt-10 md:pt-14"
      data-testid={`chapter-${c.n}`}
    >
      {/* Number */}
      <motion.div
        style={{ y: nY }}
        className="col-span-12 md:col-span-5 lg:col-span-6"
      >
        <div className="font-display font-black leading-none tracking-tighter text-[110px] sm:text-[160px] md:text-[200px] lg:text-[240px] bg-gradient-to-b from-white/95 via-white/60 to-white/10 bg-clip-text text-transparent select-none">
          {c.n}
        </div>
      </motion.div>

      {/* Content */}
      <div className="col-span-12 md:col-span-7 lg:col-span-6 md:pt-10">
        <Reveal className="flex items-center gap-2 text-[11px] font-bold tracking-[0.24em] uppercase text-orange-300">
          <c.icon className="h-3.5 w-3.5" />
          {c.kicker}
        </Reveal>
        <Reveal as="h3" delay={0.05} className="mt-4 font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-white leading-[1.05]">
          {c.title}
        </Reveal>
        <Reveal delay={0.1} className="mt-4 text-white/70 text-base md:text-lg max-w-md">
          {c.body}
        </Reveal>
      </div>
    </div>
  );
}
